import React from "react"
import Modal from "react-bootstrap/Modal"
import PropTypes from "prop-types"
import Button from "react-bootstrap/Button"
import Spinner from "react-bootstrap/Spinner"

const BaseModal = (props) => (
  <Modal.Body className="text-center">
    {props.children}
  </Modal.Body>
)

const SpinnerCard = (props) => (
  <div className="card border-light text-center" style={{maxWidth: "100%", overflow: "hidden"}}>
    <div className="card-img-top">
      {(props.submitStep > props.completedStep)
        ?
          <i className="fas fa-fw fa-check fa-lg mt-4" style={{color: 'green'}}></i>
        :
          <Spinner
            as="span"
            animation="border"
            role="status"
            aria-hidden="true"
            className="mt-3"
          />
      }
    </div>
    <div className="card-body">
      <div className="card-text" style={{wordWrap: "normal"}}>
        {props.label}
      </div>
    </div>
  </div>
)

export const LoadingModal = () => (
  <BaseModal>
    <Spinner
      as="span"
      animation="border"
      role="status"
      aria-hidden="true"
      className="mb-3"
    />
    <h6>Loading form...</h6>
  </BaseModal>
)

export const LoadingErrorModal = (props) => (
  <BaseModal>
    <h3 className="mb-3">
      <i className="fas fa-fw fa-exclamation-triangle mr-1"></i>
      Failed to find saved form data
    </h3>
    <p>We couldn't find a saved form matching the ID <code>{props.uuid}</code>.</p>
    <p>
      Try refreshing the page. If this error persists, contact
      the site administrators
      at <a href="mailto:cnda@lisc.org">cnda@lisc.org</a> to
      retrieve your saved form.
    </p>
    <Button onClick={props.closeModal}>
      Close
    </Button>
  </BaseModal>
)

export const SaveSuccessModal = (props) => (
  <BaseModal>
    <h3 className="mb-3">
      <i className="fas fa-fw fa-check mr-1" style={{color: 'green'}}></i>
      Draft saved!
    </h3>
    <p>
      Your draft has been successfully saved. To return to it
      and continue editing, <strong>visit the following unique URL</strong>.
      Make sure to save this link somewhere you'll remember it if you'd like
      to finish this application later.
    </p>
    <p>
      <a href={props.permalink}>
        {props.permalink}
      </a>
    </p>
    <a
      className="btn btn-primary link-btn"
      href={props.permalink}
    >
      Close
    </a>
  </BaseModal>
)

export const SaveErrorModal = (props) => (
  <BaseModal>
    <h3 className="mb-3">
      <i className="fas fa-fw fa-exclamation-triangle mr-1" style={{color: 'red'}}></i>
      Draft failed to save
    </h3>
    <p>
      Check your Internet connection and try again. If this
      error persists, contact the site administrators
      at <a href="mailto:cnda@lisc.org">cnda@lisc.org</a>.
    </p>
    <Button onClick={props.closeModal}>
      Close
    </Button>
  </BaseModal>
)

export const SubmitProgressModal = (props) => (
  <BaseModal>
    <h3 className="mb-3">
      Submitting application...
    </h3>
    <p>
      Hang tight while we submit your application. This process can take a few
      seconds to complete.
    </p>
    <div className="card-group">
      <SpinnerCard submitStep={props.submitStep} completedStep={1} label="Creating new application" />
      <SpinnerCard submitStep={props.submitStep} completedStep={2} label="Uploading attachments" />
      <SpinnerCard submitStep={props.submitStep} completedStep={3} label="Saving application" />
      <SpinnerCard submitStep={props.submitStep} completedStep={4} label="Saving contact info" />
    </div>
  </BaseModal>
)

export const SubmitSuccessModal = (props) => (
  <BaseModal>
    <h3 className="mb-3">
      <i className="fas fa-fw fa-check mr-1" style={{color: 'green'}}></i>
      Application submitted!
    </h3>
    <p>
      Your application has been successfully submitted. To view it
      later, <strong>visit the following unique URL</strong>.
    </p>
    <p>
      <a href={props.permalink}>
        {props.permalink}
      </a>
    </p>
    <a
      className="btn btn-primary link-btn"
      href={props.permalink}
    >
      Close
    </a>
  </BaseModal>
)

export const SubmitErrorModal = (props) => (
  <BaseModal>
    <h3 className="mb-3">
      <i className="fas fa-fw fa-exclamation-triangle mr-1" style={{color: 'red'}}></i>
      Application failed to submit
    </h3>
    <p>
      Check your Internet connection and try again. If this
      error persists, contact the site administrators
      at <a href="mailto:cnda@lisc.org">cnda@lisc.org</a>.
    </p>
    <Button onClick={props.closeModal}>
      Close
    </Button>
  </BaseModal>
)

const InfoModal = (props) => {
  let modalContent
  switch(props.status) {
    case 'loading':
      modalContent = <LoadingModal />
      break
    case 'loadingError':
      modalContent = <LoadingErrorModal uuid={props.uuid} closeModal={props.closeModal}/>
      break
    case 'saveSuccess':
      modalContent = <SaveSuccessModal permalink={props.permalink}/>
      break
    case 'saveError':
      modalContent = <SaveErrorModal closeModal={props.closeModal}/>
      break
    case 'submitProgress':
      modalContent = <SubmitProgressModal submitStep={props.submitStep} />
      break
    case 'submitSuccess':
      modalContent = <SubmitSuccessModal permalink={props.permalink}/>
      break
    case 'submitError':
      modalContent = <SubmitErrorModal closeModal={props.closeModal}/>
      break
    case 'normal':
      break
    default:
      throw new Error(`Modal status not recognized: ${props.status}`)
  }
  return (
    <Modal show={props.status !== 'normal'}>
      {modalContent}
    </Modal>
  )
}

InfoModal.propTypes = {
  status: PropTypes.oneOf(
    ['normal', 'loading', 'loadingError', 'submitProgress', 'submitSuccess',
     'submitError', 'saveSuccess', 'saveError']
  ).isRequired,
  closeModal: PropTypes.func.isRequired,
}

export default InfoModal
